'use client'

import { TestResultDetails } from '@/api/services/types/resultsTypes'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/common/Button'
import { getImageUrl } from '@/utils/getImageUrl'
import { useNavigate } from 'react-router-dom'

export default function TestResults({
  userName,
  result
}: {
  userName: string
  result: TestResultDetails
}) {
  const navigate = useNavigate()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const moduleIds = result.testData?.modules.map((a) => a.id)

  const moduleSkillsMap = moduleIds?.reduce(
    (acc, moduleId) => {
      var skillsForModule = result.moduleSkills
        .filter((skill) => skill.testModuleId === moduleId)
        .sort((a, b) => b.skillScore - a.skillScore)
      if (moduleIds[0] === moduleId) {
        const moduleSkill = result.moduleSkills.find((a) => a.testModuleId === moduleId)
        if (moduleSkill && moduleSkill?.moduleSkillPercentageScore >= 60) {
          skillsForModule = skillsForModule.slice(0, 2)
        } else {
          skillsForModule = skillsForModule.slice(0, 1)
        }
      } else {
        skillsForModule = skillsForModule.slice(0, 1)
      }
      acc[moduleId] = skillsForModule

      return acc
    },
    {} as Record<string, typeof result.moduleSkills>
  )

  const firstModuleSkills = moduleSkillsMap?.[moduleIds?.[0] || ''] || []
  const secondModuleSkills = moduleSkillsMap?.[moduleIds?.[1] || ''] || []

  const matchingCombinations = result.testSkillsCombinations?.filter((combination) => {
    const skill1Matches =
      firstModuleSkills.some(
        (skill) => skill.skill.id === combination.combinationDescriptionResponse.skill1.id
      ) ||
      secondModuleSkills.some(
        (skill) => skill.skill.id === combination.combinationDescriptionResponse.skill1.id
      )

    const skill2Matches =
      firstModuleSkills.some(
        (skill) => skill.skill.id === combination.combinationDescriptionResponse.skill2.id
      ) ||
      secondModuleSkills.some(
        (skill) => skill.skill.id === combination.combinationDescriptionResponse.skill2.id
      )
    return skill1Matches && skill2Matches
  })

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8">
      {/* Nagłówek główny */}
      <div className="mb-12 bg-gradient-to-r from-[#2bcaca]/10 to-[#2b9999]/10 p-8 text-center shadow-sm">
        <h1 className="mb-6 text-4xl font-bold tracking-tight text-[#2bcaca]">
          🧾 RAPORT – POZNAJ SIEBIE
        </h1>
        <div className="mx-auto max-w-2xl space-y-3">
          <div className="flex items-center justify-center gap-2 text-lg">
            <span className="font-semibold text-gray-700">Imię i nazwisko:</span>
            <span className="text-[#2b9999]">{userName}</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-lg">
            <span className="font-semibold text-gray-700">Data:</span>
            <span className="text-[#2b9999]">{formatDate(result.createdAt)}</span>
          </div>
          {result.testData?.title && (
            <div className="flex items-center justify-center gap-2 text-lg">
              <span className="font-semibold text-gray-700">Rodzaj testu:</span>
              <span className="text-[#2b9999]">{result.testData.title}</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-8">
        {/* Analiza modułów - sprawdzamy analyzeModulesSeparately */}
        {result.testData?.analyzeModulesSeparately &&
        result.testData?.modules?.length > 0 ? (
          // Jeśli analizujemy moduły osobno
          <>
            {result.testData?.modules?.map((module) => {
              const topSkills = moduleSkillsMap?.[module.id] || []
              return (
                <Card key={module.id} className="w-full rounded-none border-[#2b9999]/30">
                  <CardHeader>
                    <CardTitle className="text-xl">📊 {module.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {topSkills.map((skill) => (
                      <div key={skill.id}>
                        <h3 className="mb-2 text-lg font-semibold">
                          🔬 {skill.skill.name}
                        </h3>
                        <p className="leading-relaxed text-gray-700">
                          {skill.skill.description}
                        </p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )
            })}

            {/* Opis połączenia cech */}
            {matchingCombinations?.length > 0 && (
              <Card className="w-full border-[#2b9999]/30">
                <CardHeader>
                  <CardTitle className="text-xl">
                    🧠 JAKI/A JESTEŚ? (OPIS TWOJEGO PROFILU)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {matchingCombinations?.map((combination, index) => (
                    <div key={index} className="mb-4">
                      <p className="leading-relaxed whitespace-pre-line text-gray-700">
                        {combination.combinationDescriptionResponse.description}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          // Jeśli NIE analizujemy modułów osobno
          <>
            {result.testData?.purpose &&
              result.testSkills &&
              result.testSkills.length > 0 && (
                <Card className="w-full rounded-none border-[#2b9999]/30">
                  <CardHeader>
                    <CardTitle className="text-xl">
                      🎯 {result.testData.purpose}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {result.testSkills?.length > 0 && (
                      <div>
                        <h3 className="mb-3 text-lg font-semibold">
                          ✅ Najbardziej pasujące cechy:
                        </h3>
                        <div className="space-y-4">
                          {result.testSkills
                            .sort((a, b) => b.skillScore - a.skillScore)
                            .slice(0, 2)
                            .map((skillResult) => (
                              <div
                                key={skillResult.id}
                                className="border-l-4 border-[#2bcaca] pl-4">
                                <h4 className="mb-2 text-lg font-semibold">
                                  {skillResult.skill.name}
                                </h4>
                                <p className="leading-relaxed text-gray-700">
                                  {skillResult.skill.description}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

            {/* Opis połączenia cech */}
            {matchingCombinations?.length > 0 && (
              <Card className="w-full rounded-none border-[#2b9999]/30">
                <CardHeader>
                  <CardTitle className="text-xl">
                    🧠 JAKA JESTEŚ? (OPIS TWOJEGO PROFILU)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {matchingCombinations?.map((combination, index) => (
                    <div key={index} className="mb-4">
                      <p className="leading-relaxed whitespace-pre-line text-gray-700">
                        {combination.combinationDescriptionResponse.description}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Zawody */}
        {result.professions?.length > 0 && (
          <Card className="w-full rounded-none border-[#2b9999]/30">
            <CardHeader>
              <CardTitle className="text-xl">
                💼 Zawody, które mogą Ci się spodobać:
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                {result.professions.slice(0, 4).map((professionResult) => (
                  <div
                    key={professionResult.id}
                    className="flex gap-4 border border-[#2b9999]/10 p-4 transition-shadow hover:shadow-md">
                    {professionResult.profession.imageFileId && (
                      <div className="h-48 w-32 flex-shrink-0">
                        <img
                          src={getImageUrl(professionResult.profession.imageFileId)}
                          alt={professionResult.profession.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <div className="min-w-0 flex-grow">
                      <h4 className="mb-2 text-lg font-semibold">
                        {professionResult.profession.name}
                      </h4>
                      <p className="text-sm leading-relaxed text-gray-700">
                        {professionResult.profession.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tekst końcowy */}
        <Card className="w-full rounded-none border-2 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-xl text-orange-800">
              ✨ Kilka słów na zakończenie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="leading-relaxed font-medium text-orange-900">
              Twój wynik to wskazówka, która może pomóc Ci lepiej zrozumieć siebie i
              świadomie podjąć decyzję o wyborze szkoły. To, jaką drogą pójdziesz, zależy
              od Ciebie – masz wpływ na swoje decyzje. Możesz próbować, odkrywać nowe
              rzeczy i zmieniać kierunek, jeśli poczujesz, że to potrzebne. Nie musisz
              znać wszystkich odpowiedzi od razu. Najważniejsze, żeby to, co robisz, było
              bliskie Tobie i dawało Ci poczucie sensu. Twoja przyszłość to podróż – i
              możesz ją tworzyć krok po kroku, w swoim tempie. 🚀
            </p>
          </CardContent>
        </Card>
        {/* Przycisk powrotu */}
        <div className="pt-4 text-center">
          <Button
            className="bg-gradient-to-r from-[#2bcaca] to-[#2b9999] px-6 py-2 text-base font-medium text-white shadow-sm transition-all duration-200 hover:from-[#2b9999] hover:to-[#2bcaca] hover:shadow-md sm:px-8 sm:py-3"
            onClick={() => navigate('/')}>
            Powrót do ekranu głównego
          </Button>
        </div>
      </div>
    </div>
  )
}
